import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { GenerationResponse } from '../../../services/generationsApi';
import {
  regenerateBusinessPlan,
  regenerateCovers,
  regenerateLyrics,
  regeneratePresentation,
  regenerateTitle,
  regenerateWebpage,
} from '../../../services/generationsApi';

interface UseRegenerateCreationProps {
  isDesignerSession: boolean;
  isMusicGenerationSession: boolean;
  taskId: string | null;
  selectedImageUrlRef: React.MutableRefObject<string | null>;
  selectedImageIdRef: React.MutableRefObject<1 | 2 | null>;
  createImageMutation: (args: {
    prompt: string;
    imageUrl?: string;
    imageUrls?: string[];
    type?: 'album' | 'design';
  }) => Promise<{ imageUrl: string }>;
  setIsRegeneratingImage: (value: boolean) => void;
  setRegeneratedImageUrl: (url: string) => void;
  sendMessage: (data: any) => void;
  setIsRegeneratingMusic: (value: boolean) => void;
  setIsRegeneratingLyrics?: (value: boolean) => void;
  selectedSongIdRef: React.MutableRefObject<1 | 2 | null>;
  resolveMusicCardImage: (songId: 1 | 2) => string | null;
  setMusicCoverOverride: (songId: 1 | 2, imageUrl: string | null) => void;
  currentMusicPromptRef: React.MutableRefObject<string | null>;
  currentMusicStyleRef: React.MutableRefObject<string | null>;
  currentMusicThumbnailUrlRef: React.MutableRefObject<string | null>;
  setCurrentMusicContext: (context: {
    taskId: string;
    prompt: string;
    musicStyle?: string;
    thumbnailUrl?: string;
  }) => void;
  musicGenerationRef: React.MutableRefObject<
    GenerationResponse | null | undefined
  >;
  createMusicGeneration: (args: {
    prompt: string;
    musicStyle?: string;
    thumbnailUrl?: string;
    uniqueId: string;
  }) => Promise<unknown>;
  setRegeneratedTaskId: (taskId: string | null) => void;
  updateSongThumbnail?: (songVersion: 1 | 2, thumbnailUrl: string) => void;
  savedSongCreationId1Ref?: React.MutableRefObject<string | null>;
  savedSongCreationId2Ref?: React.MutableRefObject<string | null>;
  /** Sync API failures (e.g. content moderation) with ProduceErrorModal in Generation. */
  onRegenerationApiError?: (error: unknown) => void;
}

interface RegenerateArgs {
  type:
    | 'lyrics'
    | 'title'
    | 'cover'
    | 'music'
    | 'image'
    | 'presentation'
    | 'businessPlan'
    | 'webpage';
  modification_request: string;
  taskId?: string;
}

export const useRegenerateCreation = ({
  isDesignerSession,
  isMusicGenerationSession,
  taskId,
  selectedImageUrlRef,
  selectedImageIdRef,
  createImageMutation,
  setIsRegeneratingImage,
  setRegeneratedImageUrl,
  sendMessage,
  setIsRegeneratingMusic,
  setIsRegeneratingLyrics = () => undefined,
  selectedSongIdRef,
  resolveMusicCardImage,
  setMusicCoverOverride,
  currentMusicPromptRef,
  currentMusicStyleRef,
  currentMusicThumbnailUrlRef,
  setCurrentMusicContext,
  musicGenerationRef,
  createMusicGeneration,
  setRegeneratedTaskId,
  updateSongThumbnail,
  onRegenerationApiError,
}: UseRegenerateCreationProps) => {
  const queryClient = useQueryClient();

  const handleRegenerateCreation = useCallback(
    async (args: RegenerateArgs, sendFn: (msg: string) => void) => {
      try {
        if (isDesignerSession && args.type === 'image') {
          // Image regenerate (designer flow)
          const imageUrl = selectedImageUrlRef.current;
          const imageId = selectedImageIdRef.current;
          console.log(`Regenerating image ${imageId} with URL: ${imageUrl}`);
          sendFn(
            'I am working on regenerating the image based on your request. This will take a moment.',
          );
          setIsRegeneratingImage(true);
          const variationInstruction =
            ' Ensure this is a distinctly different variation from the reference image, not a duplicate or near-duplicate.';
          const promptWithVariation =
            args.modification_request + variationInstruction;
          let result: { imageUrl: string };
          try {
            result = await createImageMutation({
              prompt: promptWithVariation,
              imageUrl: imageUrl || undefined,
              type: 'design',
            });
          } catch (err) {
            onRegenerationApiError?.(err);
            sendFn(
              'I’m sorry, but I ran into an issue regenerating the image. Please try again.',
            );
            return;
          } finally {
            setIsRegeneratingImage(false);
          }
          // update regenerated image url
          console.log(
            `Image ${imageId} regenerated successfully: ${result.imageUrl}`,
          );
          console.log('Setting regeneratedImageUrl to:', result.imageUrl);
          setRegeneratedImageUrl(result.imageUrl);
          console.log('After setRegeneratedImageUrl call');
          // notify avatar silently
          const message = `The regenerated image is now ready and displayed. If user asks about the image, you can inform them that it's now available.`;
          const data = {
            type: 'send_message',
            data: {
              role: 'system',
              text: message,
              trigger_response: false,
              delay: true,
            },
          };
          console.log('Sending image completion notification to avatar');
          sendMessage(data);
          return;
        }

        if (isMusicGenerationSession) {
          if (!taskId) {
            sendFn('Cannot regenerate without a task.');
            return;
          }

          const preferredSong =
            selectedSongIdRef.current === 2
              ? 2
              : selectedSongIdRef.current === 1
              ? 1
              : 1;
          const fallbackSong: 1 | 2 = preferredSong === 1 ? 2 : 1;
          const preferredCardImage = resolveMusicCardImage(preferredSong);
          const fallbackCardImage =
            preferredSong === fallbackSong
              ? null
              : resolveMusicCardImage(fallbackSong);
          const candidateImage =
            preferredCardImage ??
            fallbackCardImage ??
            currentMusicThumbnailUrlRef.current ??
            selectedImageUrlRef.current ??
            musicGenerationRef.current?.musicGptCoverUrl ??
            null;

          const regenerateWithCandidate = async (
            sendMessageText: string,
            successMessage: string,
          ) => {
            sendFn(sendMessageText);
            setIsRegeneratingImage(true);

            try {
              const result = await createImageMutation({
                prompt: args.modification_request,
                imageUrl: candidateImage || undefined,
                type: 'album',
              });

              setMusicCoverOverride(preferredSong, result.imageUrl);

              const prompt =
                currentMusicPromptRef.current ??
                musicGenerationRef.current?.prompt ??
                '';
              const style =
                currentMusicStyleRef.current ??
                musicGenerationRef.current?.musicStyle ??
                undefined;
              const contextTaskId =
                taskId ?? musicGenerationRef.current?.taskId ?? '';

              setCurrentMusicContext({
                taskId: contextTaskId,
                prompt,
                musicStyle: style || undefined,
                thumbnailUrl: result.imageUrl,
              });

              // Update song thumbnail in database when music cover is regenerated
              if (updateSongThumbnail && preferredSong) {
                updateSongThumbnail(preferredSong, result.imageUrl);
              }

              sendFn(successMessage);
            } catch (err) {
              console.error('Music cover regeneration failed:', err);
              onRegenerationApiError?.(err);
              sendFn(
                'I’m sorry, but I ran into an issue updating the cover art. Please try again.',
              );
            } finally {
              setIsRegeneratingImage(false);
            }
          };

          if (args.type === 'image') {
            await regenerateWithCandidate(
              'I am updating the cover art based on your request. This will take a moment.',
              `I've refreshed the cover art based on your request: "${args.modification_request}".`,
            );
            return;
          }

          if (args.type === 'cover' && candidateImage) {
            await regenerateWithCandidate(
              'I am updating the cover art based on your request. This will take a moment.',
              `I've refreshed the cover art based on your request: "${args.modification_request}".`,
            );
            return;
          }

          if (args.type === 'cover') {
            sendFn(
              'I am regenerating the album cover based on your request. This will take a moment.',
            );
            setIsRegeneratingImage(true);
            try {
              // Use `modificationRequest` (not `prompt`) — server routes this to
              // the modification-specific template that preserves the existing
              // cover and applies only the requested change. The cached song
              // concept is kept server-side as internal context for the template.
              await regenerateCovers(taskId, {
                modificationRequest: args.modification_request,
              });
              queryClient.invalidateQueries({
                queryKey: ['generationStatus', taskId],
              });
              sendFn(
                'The new album cover is being generated and will appear shortly.',
              );
            } catch (err) {
              console.error('Album cover regeneration failed:', err);
              onRegenerationApiError?.(err);
              sendFn(
                'Sorry, I ran into an issue regenerating the album cover. Please try again.',
              );
            } finally {
              setIsRegeneratingImage(false);
            }
            return;
          }

          if (args.type === 'lyrics') {
            sendFn(
              'I am working on regenerating the lyrics based on your request. This will take a moment.',
            );
            setIsRegeneratingLyrics(true);
            try {
              await regenerateLyrics(taskId, {
                prompt: args.modification_request,
                instructions: args.modification_request,
              });
              queryClient.invalidateQueries({
                queryKey: ['generationStatus', taskId],
              });
            } catch (err) {
              console.error('Lyrics regeneration failed:', err);
              onRegenerationApiError?.(err);
              sendFn(
                'Sorry, I ran into an issue regenerating the lyrics. Please try again.',
              );
              setIsRegeneratingLyrics(false);
            }
            return;
          }

          if (args.type === 'title') {
            sendFn(
              'I am updating the song title based on your request. One moment.',
            );
            try {
              await regenerateTitle(taskId, {
                modification_request: args.modification_request,
              });
              queryClient.invalidateQueries({
                queryKey: ['generationStatus', taskId],
              });
              sendFn(
                `I've refreshed the title based on your request: "${args.modification_request}".`,
              );
            } catch (err) {
              console.error('Title regeneration failed:', err);
              onRegenerationApiError?.(err);
              sendFn(
                'Sorry, I ran into an issue updating the song title. Please try again.',
              );
            }
            return;
          }

          if (args.type === 'music') {
            sendFn(
              'I am working on regenerating the music based on your request. This will take a moment.',
            );
            setIsRegeneratingMusic(true);

            const basePrompt =
              currentMusicPromptRef.current ??
              musicGenerationRef.current?.prompt ??
              '';
            const style =
              currentMusicStyleRef.current ??
              musicGenerationRef.current?.musicStyle ??
              undefined;
            const thumbnail =
              currentMusicThumbnailUrlRef.current ??
              musicGenerationRef.current?.musicGptCoverUrl ??
              undefined;

            const newPrompt = basePrompt
              ? `${basePrompt}. ${args.modification_request}`
              : args.modification_request;

            const uniqueId = uuidv4();

            try {
              await createMusicGeneration({
                prompt: newPrompt,
                musicStyle: style || undefined,
                thumbnailUrl: thumbnail || undefined,
                uniqueId,
              });
              setRegeneratedTaskId(uniqueId);
            } catch (err) {
              console.error('Music regeneration failed:', err);
              throw err;
            } finally {
              setIsRegeneratingMusic(false);
            }
            return;
          }
        }

        if (args.type === 'presentation') {
          sendFn(
            'I am working on regenerating the presentation based on your request. This will take a moment.',
          );
          await regeneratePresentation({
            taskId: args.taskId || '',
            prompt: args.modification_request,
          });
          if (args.taskId) {
            queryClient.invalidateQueries({
              queryKey: ['generationStatus', args.taskId],
            });
          }
          return;
        }

        if (args.type === 'businessPlan') {
          sendFn(
            'I am working on regenerating the business plan based on your request. This will take a moment.',
          );
          await regenerateBusinessPlan({
            taskId: args.taskId || '',
            prompt: args.modification_request,
          });
          if (args.taskId) {
            queryClient.invalidateQueries({
              queryKey: ['generationStatus', args.taskId],
            });
          }
          return;
        }

        if (args.type === 'webpage') {
          sendFn(
            'I am working on regenerating the webpage based on your request. This will take a moment.',
          );
          await regenerateWebpage({
            taskId: args.taskId || '',
            prompt: args.modification_request,
          });
          if (args.taskId) {
            queryClient.invalidateQueries({
              queryKey: ['generationStatus', args.taskId],
            });
          }
          return;
        }
      } catch (err) {
        console.error('handleRegenerateCreation failed:', err);
        onRegenerationApiError?.(err);
        sendFn('Sorry, regeneration failed. Please try again.');
        setIsRegeneratingImage(false);
        setIsRegeneratingMusic(false);
        setIsRegeneratingLyrics(false);
      }
    },
    [
      isDesignerSession,
      isMusicGenerationSession,
      taskId,
      onRegenerationApiError,
      selectedImageUrlRef,
      selectedImageIdRef,
      createImageMutation,
      setIsRegeneratingImage,
      setRegeneratedImageUrl,
      sendMessage,
      setIsRegeneratingMusic,
      setIsRegeneratingLyrics,
      selectedSongIdRef,
      resolveMusicCardImage,
      setMusicCoverOverride,
      currentMusicPromptRef,
      currentMusicStyleRef,
      currentMusicThumbnailUrlRef,
      setCurrentMusicContext,
      musicGenerationRef,
      createMusicGeneration,
      setRegeneratedTaskId,
      updateSongThumbnail,
      queryClient,
    ],
  );

  return { handleRegenerateCreation };
};
